export type PreferredDevices = {
  audio?: Device;
  video?: Device;
};

export type Device = {
  id: string;
  label: string;
};

const LOCAL_STORAGE_KEY = "devices.pref";

/**
 * Get the student's preferred webcam Camera and Microphone devices.
 */
export const getStoredPreferredDevices = (): PreferredDevices => {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!raw) return {};

    return parsePreferredDevicesJson(raw);
  } catch (err) {
    console.error(err);
  }

  return {};
};

/**
 * Set the student's preferred webcam Camera and Microphone devices.
 */
export const setStoredPreferredDevices = (devices: PreferredDevices) => {
  try {
    const serialised = JSON.stringify(devices);
    localStorage.setItem(LOCAL_STORAGE_KEY, serialised);
  } catch (err) {
    console.error(err);
  }
};

const parsePreferredDevicesJson = (raw: string): PreferredDevices => {
  const parsed: unknown = JSON.parse(raw);
  if (!isObject(parsed)) return {};

  const devices: PreferredDevices = {};

  if (isDevice(parsed.audio)) {
    devices.audio = parsed.audio;
  }

  if (isDevice(parsed.video)) {
    devices.video = parsed.video;
  }

  return devices;
};

const isObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isDevice = (value: unknown): value is Device => {
  if (!isObject(value)) return false;

  return typeof value.id === "string" && typeof value.label === "string";
};

const CAMERA_ENABLED_KEY = "devices.cameraEnabled";

/**
 * Get the student's persisted camera-enabled preference.
 * Returns null when no preference has been stored yet.
 */
export const getStoredCameraEnabled = (): boolean | null => {
  try {
    const raw = localStorage.getItem(CAMERA_ENABLED_KEY);
    if (raw === null) return null;
    return raw === "true";
  } catch (err) {
    console.error(err);
  }

  return null;
};

/**
 * Persist the student's camera-enabled preference.
 */
export const setStoredCameraEnabled = (enabled: boolean) => {
  try {
    localStorage.setItem(CAMERA_ENABLED_KEY, String(enabled));
  } catch (err) {
    console.error(err);
  }
};
